import { ActionCenterViewHandler } from '@/hooks/useActionCenterViewController';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
interface BudgetsHandlerOptions {
  userId?: number;
  setSelectedItemId: (id: string) => void;
  setIsDialogOpen: (open: boolean) => void;
  setFocusDepartmentId: (id: number | undefined) => void;
  setFocusSystemId: (id: number | undefined) => void;
  setTabById: (tabId: string) => void;
  ownedSystems?: Array<{ id: number }>; // Systems owned by the user
  systemsLoading?: boolean; // Whether systems are still loading
}

function resolveTabId(
  item: ActionCenterItemV2,
  options: Pick<BudgetsHandlerOptions, 'userId' | 'ownedSystems' | 'systemsLoading'>,
): { tabId: string | null; departmentId?: number; systemId?: number } {
  const scope = (item.scope || '').toString().trim().toLowerCase();
  const currentUserId = options.userId;
  const isSystemOwner =
    currentUserId !== undefined &&
    Number(item.responsible_id) === Number(currentUserId);
  const isDepartmentOwner =
    currentUserId !== undefined &&
    Number(item.supervisor_id) === Number(currentUserId);

  if (scope === 'system' || scope === 'systems') {
    // For system owners, ONLY route to systems tab if we can CONFIRM the system is in owned systems.
    // Otherwise, default to departments tab to avoid intermediate states.
    if (isSystemOwner) {
      const ownedSystems = options.ownedSystems;
      
      // If systems are still loading, we can't confirm the system is in owned systems, so default to departments
      if (options.systemsLoading) {
        return {
          tabId: 'departments',
          systemId: item.system_id || undefined,
          departmentId: item.department_id || undefined,
        };
      }
      
      // Check if user has any owned systems AND if this specific system exists in owned systems
      const hasOwnedSystems = ownedSystems && ownedSystems.length > 0;
      const systemExistsInOwned = hasOwnedSystems && ownedSystems
        ? ownedSystems.some(sys => sys.id === item.system_id)
        : false;
      
      if (systemExistsInOwned) {
        // System is confirmed to be in owned systems, use systems tab
        return {
          tabId: 'systems',
          systemId: item.system_id || undefined,
          departmentId: item.department_id || undefined,
        };
      } else {
        // System owner but system not confirmed in owned systems (either no owned systems or system not in list), use departments tab
        return {
          tabId: 'departments',
          systemId: item.system_id || undefined,
          departmentId: item.department_id || undefined,
        };
      }
    }
    if (isDepartmentOwner) {
      return {
        tabId: 'departments',
        systemId: item.system_id || undefined,
        departmentId: item.department_id || undefined,
      };
    }
    // Default fallback: try systems tab first
    return {
      tabId: 'systems',
      systemId: item.system_id || undefined,
      departmentId: item.department_id || undefined,
    };
  }

  if (scope === 'department' || scope === 'departments') {
    return {
      tabId: 'departments',
      departmentId: item.department_id || undefined,
    };
  }

  if (scope === 'global') {
    return { tabId: 'global' };
  }

  return { tabId: null };
}

/**
 * Builds the Budgets tab-specific handler that focuses the correct tab and
 * opens the Action Item dialog when a selection is triggered.
 */
export function createBudgetsActionCenterHandler({
  userId,
  setSelectedItemId,
  setIsDialogOpen,
  setFocusDepartmentId,
  setFocusSystemId,
  setTabById,
  ownedSystems,
  systemsLoading,
}: BudgetsHandlerOptions): ActionCenterViewHandler {
  return ({ item, clearSelection }) => {
    setSelectedItemId(String(item.id));
    setIsDialogOpen(true);
    setFocusDepartmentId(undefined);
    setFocusSystemId(undefined);

    const { tabId, departmentId, systemId } = resolveTabId(item, { userId, ownedSystems, systemsLoading });

    if (typeof departmentId !== 'undefined') {
      setFocusDepartmentId(departmentId);
    }
    if (typeof systemId !== 'undefined') {
      setFocusSystemId(systemId);
    }

    if (tabId) {
      setTabById(tabId);
    }

    // Delay clearing selection to allow components to read focusSystemId/focusDepartmentId
    // This ensures the selection is available when components render with initialSystemId/initialDepartmentId
    setTimeout(() => {
      clearSelection();
    }, 100);
  };
}

